import { CheckCircleFilled } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useAppStateStore } from '@client/stores/AppStateStore';
import { trpcClient } from '@client/trpc/client';
import { sleep } from '@client/utils';
import { buildSameOriginRedirectUrl } from '@client/utils/routing/safe-redirect-url';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Typography } from 'antd';
import { useEffect } from 'react';
import { z } from 'zod';

const validateSearch = z.object({
    ol: z.string().optional(),
});

export const Route = createFileRoute('/maintenance')({
    component: Maintenance,
    validateSearch: zodValidator(validateSearch),
});

function Maintenance() {
    const inMaintenanceMode = useFeatureFlag('in-maintenance-mode');
    const redirectDestinationAfterMaintenance = useFeatureFlag<string>(
        'redirect-destination-after-maintenance',
        'previous-location',
    );
    const navigate = useNavigate();
    const appState = useAppStateStore.getState();
    const { ol } = Route.useSearch();

    useEffect(() => {
        const redirect = async () => {
            if (inMaintenanceMode) {
                return;
            }

            await sleep(3000);

            const appBuildVersion = await trpcClient.appSetting.getAppBuildVersion.query();

            // Refresh the page to clear any cached resources
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((name) => caches.delete(name)));
            }

            if (appBuildVersion) {
                appState.actions.setAppBuildNumber(appBuildVersion.version);
                appState.actions.setAppBuildLastUpdatedAt(appBuildVersion.updatedAt);
            }

            if (redirectDestinationAfterMaintenance === 'previous-location') {
                const safeRedirectUrl = ol
                    ? buildSameOriginRedirectUrl({
                          redirectUrl: ol,
                          origin: window.location.origin,
                          timestamp: Date.now(),
                      })
                    : null;

                if (safeRedirectUrl) {
                    window.location.href = safeRedirectUrl;
                } else {
                    navigate({ to: '/' });
                }
            } else if (redirectDestinationAfterMaintenance === 'homepage') {
                navigate({ to: '/' });
            } else if (redirectDestinationAfterMaintenance === 'login') {
                navigate({ to: '/login' });
            }
        };

        void redirect();
    }, [inMaintenanceMode, redirectDestinationAfterMaintenance]);

    return (
        <PageController route={Route}>
            {inMaintenanceMode ? (
                <div className="flex h-screen w-full items-center justify-center bg-warning">
                    <div className="text-lg">
                        <Typography.Title level={2}>Under Maintenance</Typography.Title>
                        <Typography.Text>The site is undergoing upgrade. We'll be back soon.</Typography.Text>
                    </div>
                </div>
            ) : (
                <div className="flex h-screen w-full items-center justify-center bg-green-100">
                    <div className="text-center text-lg">
                        <CheckCircleFilled className="mb-4 text-6xl text-green-500" />
                        <Typography.Title level={2}>Maintenance Completed</Typography.Title>
                        <Typography.Text className="mb-2 block">
                            The system has been successfully updated and is now back online.
                        </Typography.Text>
                        <Typography.Text type="secondary" italic>
                            You will be redirected back to the application shortly...
                        </Typography.Text>
                    </div>
                </div>
            )}
        </PageController>
    );
}
